import { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Download, PlusSquare, Share2, Smartphone, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const DISMISS_KEY = "kraveon_install_prompt_dismissed_until";
const INSTALLED_KEY = "kraveon_install_prompt_installed";
const DISMISS_DAYS = 7;

const isStandaloneMode = () => {
	if (typeof window === "undefined") return false;
	return (
		window.matchMedia("(display-mode: standalone)").matches ||
		window.navigator.standalone === true
	);
};

const getMobilePlatform = () => {
	if (typeof window === "undefined") return "other";
	const ua = window.navigator.userAgent.toLowerCase();
	const isIOS =
		/iphone|ipad|ipod/.test(ua) ||
		(window.navigator.platform === "MacIntel" &&
			window.navigator.maxTouchPoints > 1);
	if (isIOS) return "ios";
	if (/android/.test(ua)) return "android";
	return "other";
};

export function MobileInstallPrompt({ role }) {
	const { t } = useLanguage();
	const [visible, setVisible] = useState(false);
	const [platform, setPlatform] = useState("other");
	const [deferredPrompt, setDeferredPrompt] = useState(null);
	const [showAndroidManual, setShowAndroidManual] = useState(false);

	const isAthlete = role === "athlete";

	const canShowPrompt = useMemo(() => {
		if (!isAthlete) return false;
		if (typeof window === "undefined") return false;
		if (isStandaloneMode()) return false;
		if (localStorage.getItem(INSTALLED_KEY) === "1") return false;
		const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || "0");
		if (dismissedUntil && Date.now() < dismissedUntil) return false;
		const detectedPlatform = getMobilePlatform();
		return detectedPlatform === "ios" || detectedPlatform === "android";
	}, [isAthlete]);

	useEffect(() => {
		if (!canShowPrompt) return;

		const detectedPlatform = getMobilePlatform();
		setPlatform(detectedPlatform);

		const showTimer = setTimeout(() => {
			setVisible(true);
		}, 1500);

		const onBeforeInstallPrompt = (event) => {
			event.preventDefault();
			setDeferredPrompt(event);
			setPlatform("android");
		};

		const onInstalled = () => {
			localStorage.setItem(INSTALLED_KEY, "1");
			setVisible(false);
		};

		window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
		window.addEventListener("appinstalled", onInstalled);

		return () => {
			clearTimeout(showTimer);
			window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
			window.removeEventListener("appinstalled", onInstalled);
		};
	}, [canShowPrompt]);

	const dismissForAWeek = () => {
		const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
		localStorage.setItem(DISMISS_KEY, String(until));
		setVisible(false);
	};

	const handleInstallClick = async () => {
		if (platform === "android" && deferredPrompt) {
			deferredPrompt.prompt();
			const choice = await deferredPrompt.userChoice;
			setDeferredPrompt(null);
			if (choice?.outcome === "accepted") {
				localStorage.setItem(INSTALLED_KEY, "1");
				setVisible(false);
				return;
			}
		}

		if (platform === "android") {
			setShowAndroidManual(true);
		}
	};

	if (!visible || !canShowPrompt) {
		return null;
	}

	return (
		<div
			className="fixed left-3 right-3 z-40 md:hidden"
			style={{ bottom: "calc(88px + var(--safe-area-bottom-dynamic))" }}
		>
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-4 space-y-3">
					<div className="flex items-start justify-between gap-3">
						<div className="flex items-center gap-2">
							<Smartphone className="w-4 h-4 text-volt" />
							<p className="font-heading text-sm">{t("installPromptTitle")}</p>
						</div>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="h-7 w-7 rounded-sm"
							onClick={dismissForAWeek}
						>
							<X className="w-4 h-4" />
						</Button>
					</div>

					<p className="text-xs text-muted-foreground">
						{t("installPromptSubtitle")}
					</p>

					{platform === "ios" && (
						<div className="space-y-2 text-xs text-muted-foreground">
							<p className="flex items-center gap-2">
								<Share2 className="w-3.5 h-3.5 text-volt" />
								{t("installPromptIosStep1")}
							</p>
							<p className="flex items-center gap-2">
								<PlusSquare className="w-3.5 h-3.5 text-volt" />
								{t("installPromptIosStep2")}
							</p>
						</div>
					)}

					{platform === "android" && (
						<div className="space-y-2 text-xs text-muted-foreground">
							{showAndroidManual && <p>{t("installPromptAndroidFallback")}</p>}
							<div className="flex items-center gap-2">
								<Button
									type="button"
									onClick={handleInstallClick}
									className="h-8 rounded-sm bg-volt hover:bg-volt/90"
								>
									<Download className="w-3.5 h-3.5 mr-1.5" />
									{t("installApp")}
								</Button>
								<Button
									type="button"
									variant="outline"
									className="h-8 rounded-sm border-dashed"
									onClick={dismissForAWeek}
								>
									{t("installPromptLater")}
								</Button>
							</div>
						</div>
					)}

					{platform === "ios" && (
						<div className="flex items-center gap-2">
							<Button
								type="button"
								variant="outline"
								className="h-8 rounded-sm border-dashed"
								onClick={dismissForAWeek}
							>
								{t("installPromptLater")}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
